import React, {useState} from 'react'
import {Modal} from 'react-bootstrap'
import {toast} from 'react-toastify'
// import {Tooltip} from 'react-tooltip'
import {KTIcon} from '../../../_metronic/helpers'
import {useAuth} from '../../modules/auth'
import {ToolbarActionButton} from '../../../ui/ToolbarActionButton'
import DashboardApiTableShow, {DashboardColumn} from './DashboardApiTableShow'
import {ChannelInventoryFeedStatusChangeItem} from './dashboardTableTypes'
import {
  formatDashboardBoolean,
  formatDashboardCurrency,
  formatDashboardDateTime,
  renderDashboardLinkIcon,
} from './dashboardFormatters'

// Automated updates are written by system users (e.g. auto_pricing_rule); anything else
// (ui_manual_update or a named user such as brian.chin) is a manual change.
const getChangeMode = (mbUser: string | null | undefined): 'Auto' | 'Manual' | '-' => {
  if (!mbUser) return '-'
  return mbUser.toLowerCase().startsWith('auto') ? 'Auto' : 'Manual'
}

const API_URL = process.env.REACT_APP_KT_API_URL

// Downloads the latest feed file (the one sent to the FTP) through the authenticated backend endpoint.
const DownloadFeedFileButton: React.FC<{latestFile?: {file_name: string; created_at: string} | null}> = ({
  latestFile,
}) => {
  const [downloading, setDownloading] = useState(false)

  const handleDownload = async () => {
    setDownloading(true)
    try {
      const token = JSON.parse(localStorage.getItem('kt-auth-react-v') as string)
      const response = await fetch(
        `${API_URL}/dashboard/ChannelInventoryFeedStatusChanges/latest-file/download`,
        {headers: {Authorization: `Bearer ${token?.access_token}`}}
      )
      if (!response.ok) {
        const err = await response.json().catch(() => null)
        throw new Error(err?.message || 'Failed to download the file')
      }
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = latestFile?.file_name || 'channel_inventory_feed.txt'
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error: any) {
      toast.error(error?.message || 'Failed to download the file', {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      })
    } finally {
      setDownloading(false)
    }
  }

  return (
    <span
      title={
        latestFile ? `${latestFile.file_name} (${formatDashboardDateTime(latestFile.created_at)})` : 'No file available'
      }
    >
      <ToolbarActionButton
        onClick={handleDownload}
        loading={downloading}
        loadingText='Downloading...'
        disabled={!latestFile}
        icon={
          <svg
            className='me-2'
            xmlns='http://www.w3.org/2000/svg'
            width='16'
            height='16'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
            aria-hidden='true'
          >
            <path d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' />
            <path d='m7 10 5 5 5-5' />
            <path d='M12 15V3' />
          </svg>
        }
        label='Download Most Recently Uploaded File'
      />
    </span>
  )
}

// System values get friendly labels; the raw value stays available on hover. Named users show as-is.
const UPDATED_BY_LABELS: Record<string, string> = {
  ui_manual_update: 'Manual Update',
  auto_pricing_rule: 'Automatic',
}

const renderUpdatedBy = (mbUser: string | null | undefined): React.ReactNode => {
  if (!mbUser) return '-'
  const label = UPDATED_BY_LABELS[mbUser]
  if (!label) return mbUser
  const isAuto = getChangeMode(mbUser) === 'Auto'
  return (
    <span
      title={mbUser}
      className='d-inline-flex align-items-center'
    >
      <KTIcon iconName={isAuto ? 'arrows-circle' : 'profile-circle'} className='fs-5 me-1' />
      {label}
    </span>
  )
}

// Header (info icon currently commented out) explaining what TRUE / FALSE mean for the active status.
const ActiveHeader: React.FC = () => (
  <span className='d-inline-flex align-items-center'>
    Current Status
    {/*
    <span
      className='ms-2 d-inline-flex'
      data-tooltip-id='active-status-info'
      data-tooltip-html={`
        <div style="text-transform:none; line-height:18px; text-align:start">
          <ul style="margin:0; padding-left:16px;">
            <li><b>TRUE</b> — Product is live and should remain in the inventory.</li>
            <li><b>FALSE</b> — Product should be removed from the inventory.</li>
          </ul>
        </div>
      `}
      onClick={(e) => e.stopPropagation()}
    >
      <Tooltip id='active-status-info' />
      <svg xmlns='http://www.w3.org/2000/svg' width='20px' height='20px' viewBox='0 0 24 24' version='1.1'>
        <g stroke='none' strokeWidth='1' fill='none' fillRule='evenodd'>
          <rect x='0' y='0' width='24' height='24' />
          <circle fill='#000000' opacity='0.2' cx='12' cy='12' r='10' />
          <rect fill='#000000' opacity='0.5' x='11' y='10' width='2' height='7' rx='1' />
          <rect fill='#000000' opacity='0.5' x='11' y='7' width='2' height='2' rx='1' />
        </g>
      </svg>
    </span>
    */}
  </span>
)

const toPrice = (value: number | string | null | undefined): number | null => {
  if (value === null || value === undefined || value === '') return null
  const n = Number(value)
  return Number.isNaN(n) ? null : n
}

const SectionLabel: React.FC<{children: React.ReactNode}> = ({children}) => (
  <div className='text-gray-600 fw-bold fs-7 text-uppercase mb-2' style={{letterSpacing: '0.06em'}}>
    {children}
  </div>
)

// TRUE/FALSE pill with a status dot; `muted` is used for the "from" side of a change.
const StatusPill: React.FC<{value: string | boolean | null | undefined}> = ({value}) => {
  const label = formatDashboardBoolean(value)
  if (label === '-') return <span className='text-gray-600 fw-semibold'>-</span>
  const isTrue = label === 'TRUE'
  return (
    <span
      className={`d-inline-flex align-items-center gap-2 rounded-pill px-3 py-1 fs-7 fw-bold ${
        isTrue ? 'bg-light-success text-success' : 'bg-light-danger text-danger'
      }`}
    >
      <span
        className={`rounded-circle ${isTrue ? 'bg-success' : 'bg-danger'}`}
        style={{width: 7, height: 7, display: 'inline-block'}}
      />
      {label}
    </span>
  )
}

const PriceCard: React.FC<{label: string; value: number | string | null | undefined}> = ({label, value}) => (
  <div className='border border-gray-300 rounded p-4 h-100'>
    <div className='text-gray-600 fw-semibold fs-6 mb-1'>{label}</div>
    <div className='text-gray-900 fw-bolder fs-2'>{formatDashboardCurrency(value)}</div>
  </div>
)

// "Scraped price is $30.01 (15.8%) higher than Amazon" - hidden when either price is missing.
const PriceDifference: React.FC<{amazon: number | null; scraped: number | null}> = ({amazon, scraped}) => {
  if (amazon === null || scraped === null) return null
  if (amazon === scraped) {
    return <div className='text-gray-600 fs-6 mt-3'>Scraped price is the same as Amazon.</div>
  }
  const diff = Math.abs(scraped - amazon)
  const percent = amazon !== 0 ? (diff / amazon) * 100 : null
  const higher = scraped > amazon
  return (
    <div className='d-flex align-items-center gap-2 fs-6 text-gray-700 mt-3'>
      <svg
        width='16'
        height='16'
        viewBox='0 0 24 24'
        fill='none'
        stroke={higher ? '#6fcf97' : '#f08a8a'}
        strokeWidth='2.5'
        strokeLinecap='round'
        strokeLinejoin='round'
        aria-hidden='true'
      >
        {higher ? <path d='M12 20V4M5 11l7-7 7 7' /> : <path d='M12 4v16M5 13l7 7 7-7' />}
      </svg>
      <span>
        Scraped price is{' '}
        <span className={`fw-bold ${higher ? 'text-success' : 'text-danger'}`}>
          {formatDashboardCurrency(diff)}
          {percent !== null ? ` (${percent.toFixed(1)}%)` : ''} {higher ? 'higher' : 'lower'}
        </span>{' '}
        than Amazon
      </span>
    </div>
  )
}

const DetailListRow: React.FC<{label: string; children: React.ReactNode; last?: boolean}> = ({
  label,
  children,
  last,
}) => (
  <div
    className={`d-flex justify-content-between align-items-center px-4 py-3 ${
      last ? '' : 'border-bottom border-gray-300'
    }`}
  >
    <span className='text-gray-600 fw-semibold fs-6'>{label}</span>
    {children}
  </div>
)

const ViewDetailsCell: React.FC<{item: ChannelInventoryFeedStatusChangeItem}> = ({item}) => {
  const [show, setShow] = useState(false)
  const amazon = toPrice(item.amazon_price)
  const scraped = toPrice(item.scraped_price)
  const mode = getChangeMode(item.mb_user)
  const currentLabel = formatDashboardBoolean(item.active)
  // A recorded status change always flips the value, so the previous status is the opposite of the current one.
  const previousLabel = currentLabel === 'TRUE' ? 'FALSE' : currentLabel === 'FALSE' ? 'TRUE' : '-'
  const changedBy =
    mode === 'Auto'
      ? `Automatically by ${item.mb_user}`
      : item.mb_user === 'ui_manual_update'
      ? 'Manually by a user'
      : `Manually by ${item.mb_user}`

  return (
    <>
      <button
        type='button'
        className='btn btn-sm btn-light-primary d-inline-flex align-items-center'
        title='View details'
        onClick={(e) => {
          e.currentTarget.blur()
          setShow(true)
        }}
      >
        <KTIcon iconName='eye' className='fs-3 me-1' />
        Details
      </button>
      <Modal show={show} onHide={() => setShow(false)} centered restoreFocus={false}>
        <Modal.Header closeButton className='border-bottom'>
          <div className='d-flex align-items-center gap-3'>
            <span
              className='d-inline-flex align-items-center justify-content-center rounded bg-light-primary text-primary'
              style={{width: 40, height: 40}}
            >
              <KTIcon iconName='arrows-circle' className='fs-2 text-primary' />
            </span>
            <div>
              <Modal.Title className='fs-3 mb-0'>Status Change Details</Modal.Title>
              <div className='text-gray-600 fs-6'>
                <span className='text-gray-800 fw-semibold'>{item.sku}</span> &bull; {item.channel}
              </div>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body>
          {mode !== '-' && currentLabel !== '-' && (
            <div className='d-flex justify-content-between align-items-center border border-gray-300 rounded px-4 py-3 mb-6 gap-3 flex-wrap'>
              <div>
                <div className='text-gray-600 fw-bold fs-7 text-uppercase' style={{letterSpacing: '0.06em'}}>
                  Status Changed
                </div>
                <div className='text-gray-800 fs-6'>{changedBy}</div>
              </div>
              <div className='d-flex align-items-center gap-2'>
                <StatusPill value={previousLabel} />
                <svg
                  width='14'
                  height='14'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  className='text-gray-700'
                  aria-hidden='true'
                >
                  <path d='M4 12h16M14 6l6 6-6 6' />
                </svg>
                <StatusPill value={currentLabel} />
              </div>
            </div>
          )}

          <SectionLabel>Price Comparison</SectionLabel>
          <div className='row g-3'>
            <div className='col-6'>
              <PriceCard label='Amazon Price' value={item.amazon_price} />
            </div>
            <div className='col-6'>
              <PriceCard label='Scraped Price' value={item.scraped_price} />
            </div>
          </div>
          <PriceDifference amazon={amazon} scraped={scraped} />

          <div className='mt-6'>
            <SectionLabel>Details</SectionLabel>
            <div className='border border-gray-300 rounded'>
              <DetailListRow label='FTP Original Active Status'>
                <StatusPill value={item.original_active} />
              </DetailListRow>
              <DetailListRow label='Current Active Status'>
                <StatusPill value={item.active} />
              </DetailListRow>
              <DetailListRow label='Change Type' last>
                <span
                  className={`rounded px-3 py-1 fs-7 fw-bold ${
                    mode === 'Auto' ? 'bg-light-primary text-primary' : 'bg-light-warning text-warning'
                  }`}
                >
                  {mode}
                </span>
              </DetailListRow>
            </div>
          </div>

          <div className='mt-6'>
            <SectionLabel>Update Reason</SectionLabel>
            <div className='bg-light rounded px-4 py-3 text-gray-800 fs-6' style={{wordBreak: 'break-word'}}>
              {item.status_change_reason || '-'}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button type='button' className='btn btn-light' onClick={() => setShow(false)}>
            Close
          </button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

const columns: DashboardColumn<ChannelInventoryFeedStatusChangeItem>[] = [
  {header: 'SKU', sortKey: 'sku', render: (item) => <span className='font-monospace fw-semibold'>{item.sku ?? '-'}</span>},
  {header: 'Channel', sortKey: 'channel', render: (item) => item.channel ?? '-'},
  // {header: 'Type', sortKey: 'type', render: (item) => item.type ?? '-'},
  {header: <ActiveHeader />, sortKey: 'active', render: (item) => <StatusPill value={item.active} />},
  {header: 'Updated By', sortKey: 'mb_user', render: (item) => renderUpdatedBy(item.mb_user)},
  {header: 'Changed At', sortKey: 'changed_at', width: 220, render: (item) => {
      const [datePart, timePart] = formatDashboardDateTime(item.changed_at).split(', ')
      return (
        <div className='lh-sm'>
          <div className='fw-semibold'>{datePart}</div>
          {timePart && <div className='text-gray-600 fs-8'>{timePart}</div>}
        </div>
      )
    }},
  {
    header: 'Action',
    render: (item) => (
      <div className='d-flex align-items-center gap-2'>
        {renderDashboardLinkIcon(item.broken_link)}
        <ViewDetailsCell item={item} />
      </div>
    ),
  },
]

function ChannelInventoryFeedStatusChangesShow() {
  const {currentUser} = useAuth()
  // Role ids 1 and 2 are super admin / admin
  const roleId = currentUser?.roles?.[0]?.id
  const isAdmin = roleId === 1 || roleId === 2

  return (
    <DashboardApiTableShow<ChannelInventoryFeedStatusChangeItem>
      endpoint='ChannelInventoryFeedStatusChanges'
      title='Channel Inventory Feed Status Changes'
      columns={columns}
      renderToolbarExtras={
        isAdmin ? (data) => <DownloadFeedFileButton latestFile={data?.latest_file} /> : undefined
      }
    />
  )
}

export default ChannelInventoryFeedStatusChangesShow
